import React, { useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import AutomationBindingInput from './AutomationBindingInput';
import { isBinding, filterVariablesForParam } from '@/utils/automationBuilder';

/**
 * PersonTargetInput — explicit person target for v2 actions (§14.1/§14.5:
 * no hidden default person). Two modes: bind a personID-typed variable
 * (trigger/input/upstream output) or pick a specific person.
 *
 * Value shape mirrors the config contract: { var: key } binding, a literal
 * person UUID, or a people-picker object ({id,...}) which the caller should
 * normalize — we emit person objects via onPersonChange for literal mode.
 */
const PersonTargetInput = ({
    variables = [],
    value,
    onUpdate,
    onPersonChange,
    personValue,
    title = 'Person',
    required,
    testID,
    fromScreen,
    fromStoreKey,
}) => {
    const personVars = filterVariablesForParam(variables, { semantic: 'personID' });
    const [mode, setMode] = useState(isBinding(value) ? 'variable' : 'specific');

    const showVariable = mode === 'variable';
    const showSpecific = mode === 'specific';

    return (
        <View>
            <View style={styles.modeRow}>
                <TouchableOpacity
                    style={styles.modeBtn(showVariable)}
                    onPress={() => setMode('variable')}
                    testID={`${testID || title}-mode-variable`}
                >
                    <Text style={styles.modeBtnText(showVariable)}>Variable</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={styles.modeBtn(showSpecific)}
                    onPress={() => setMode('specific')}
                    testID={`${testID || title}-mode-specific`}
                >
                    <Text style={styles.modeBtnText(showSpecific)}>Specific person</Text>
                </TouchableOpacity>
            </View>

            {showVariable ? (
                <AutomationBindingInput
                    title={title}
                    placeholder="Bind a person variable..."
                    variables={personVars}
                    value={isBinding(value) ? value : undefined}
                    onUpdate={onUpdate}
                    required={required}
                    allowManualEntry={false}
                    testID={testID}
                />
            ) : (
                <SheetSelector
                    type="people"
                    title={title}
                    placeholder="Select a person"
                    value={personValue || null}
                    onChange={onPersonChange}
                    required={required}
                    fromScreen={fromScreen}
                    fromStoreKey={fromStoreKey}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    modeRow: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: 8,
    },
    modeBtn: (active) => ({
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: active ? theme.colors.primaryContainer : theme.colors.surfaceVariant,
    }),
    modeBtnText: (active) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: active ? theme.colors.primary : theme.colors.textSecondary,
    }),
}));

export default PersonTargetInput;
